import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isMethodAbortError } from '../../../lib/hooks';
import {
  perfDrillDataMatches,
  perfDrillIsPending,
  useOpsOpportunityNarrative,
  useOpsPerformanceDrillDown,
  type SourceFilter,
} from '../hooks/useOpsCommand';
import type {
  ConnectedSourceId,
  DrillDownData,
  DrillPanel,
  PerfSettings,
  ShiftFilter,
  StatusFilter,
} from '../types';
import { DrillLoadError } from './DrillErrorOverlay';
import { PerformanceDrillDown } from './PerformanceDrillDown';
import { clearDrillFocus } from './drill/crux/useDrillFocus';
import {
  DRILL_TABS,
  DrillMetricTabs,
  drillPairFor,
  drillTabLedBy,
  type DrillTabDef,
} from './shared/DrillMetricTabs';
import { Icon } from './shared/Icon';

/**
 * The drawer's deck keeps its own inspector memory, so opening it on top of
 * the Performance drill never reads, drops or clears the drill's own.
 */
export const OUTLET_DECK_FOCUS_SCOPE = 'outlet';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/** Starting metric: the given one when it has a drill tab, else covers. */
export function outletDeckStartTab(metricKey?: string | null): DrillTabDef {
  const pair = metricKey ? drillPairFor(metricKey) : null;
  return pair ? drillTabLedBy(pair.tab, pair.primary) : DRILL_TABS[0];
}

/**
 * One outlet's drill deck in a drawer: the same tiles as the Performance drill
 * (trend with compare, source mix, drivers, …) scoped to the outlet, with the
 * metric switcher on top. Presentational: data and loading come in as props.
 */
export function OutletDeckDrawerView({
  outletId,
  outletName,
  outletMeta,
  periodLabel,
  activeMetric,
  onSwitchMetric,
  connected,
  data,
  loading,
  onRetry,
  onClose,
  bucketLabels,
  aggregationLabel,
  comparable,
  opportunityNarrative,
  opportunityNarrativeLoading,
}: {
  outletId: string;
  outletName: string;
  /** Region and city. */
  outletMeta?: string;
  periodLabel: string;
  /** Metric key of the active tab. */
  activeMetric: string;
  onSwitchMetric: (tab: DrillTabDef) => void;
  connected?: ConnectedSourceId[];
  /** The outlet's drill for `activeMetric`, once it is ready. */
  data?: DrillDownData | null;
  loading: boolean;
  /** Refetch after a failed request. */
  onRetry?: () => void;
  onClose: () => void;
  bucketLabels?: string[];
  aggregationLabel?: string;
  comparable?: boolean;
  opportunityNarrative?: Extract<DrillPanel, { kind: 'narrative' }> | null;
  opportunityNarrativeLoading?: boolean;
}) {
  const { t } = useTranslation('performance');
  const titleId = useId();
  const drawerRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  const close = () => {
    clearDrillFocus(OUTLET_DECK_FOCUS_SCOPE);
    onClose();
  };

  // The drawer's memory lives as long as the drawer, however it closes.
  useEffect(() => () => clearDrillFocus(OUTLET_DECK_FOCUS_SCOPE), []);

  // Modal keyboard contract: focus moves in on open and back on close, Tab
  // stays inside, Escape closes the deck's open inspector first, then the
  // drawer. Listening on the document runs before the decks' window
  // listeners, so a drill underneath never sees an Escape meant for here.
  useEffect(() => {
    const node = drawerRef.current;
    if (!node) return;
    const opener =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    node.focus({ preventScroll: true });
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target;
      // Another dialog (tour, maître d') has the keyboard: leave it alone.
      if (
        target instanceof Node &&
        target !== document.body &&
        target !== document.documentElement &&
        !node.contains(target)
      ) {
        return;
      }
      if (event.key === 'Escape') {
        // The deck closes its own inspector on Escape; let it.
        if (node.querySelector('.ddrill-inspector')) return;
        event.stopPropagation();
        clearDrillFocus(OUTLET_DECK_FOCUS_SCOPE);
        closeRef.current();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = [...node.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
        el => !el.closest('[hidden], [aria-hidden="true"]'),
      );
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      const at = document.activeElement;
      if (event.shiftKey && (at === first || at === node)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && at === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    };
  }, []);

  const meta = [outletMeta, periodLabel].filter(Boolean).join(' · ');

  return (
    <>
      <div
        className="scrim outlet-deck-scrim"
        onClick={close}
        role="presentation"
      />
      <div
        ref={drawerRef}
        className="drawer outlet-deck-drawer"
        data-ftue="outlet-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className="dr-head">
          <div>
            <h2 id={titleId}>{outletName}</h2>
            {meta ? <p>{meta}</p> : null}
          </div>
          <button
            type="button"
            className="ghost-btn"
            onClick={close}
            aria-label={t('drawer.close')}
          >
            <Icon name="close" size={18} />
          </button>
        </div>
        <div className="outlet-deck-tabs">
          <DrillMetricTabs
            active={activeMetric}
            connected={connected}
            onSelect={onSwitchMetric}
          />
        </div>
        {data ? (
          <PerformanceDrillDown
            key={data.key}
            variant="drawer"
            focusScope={OUTLET_DECK_FOCUS_SCOPE}
            data={data}
            periodLabel={periodLabel}
            scopeLabel={outletName}
            onClose={close}
            onSwitchMetric={onSwitchMetric}
            showMetricTabs={false}
            connected={connected}
            bucketLabels={bucketLabels}
            aggregationLabel={aggregationLabel}
            comparable={comparable}
            opportunityNarrative={opportunityNarrative}
            opportunityNarrativeLoading={opportunityNarrativeLoading}
            agentCtx={{ metricKey: data.key, outletId }}
          />
        ) : (
          <div className="outlet-deck-state">
            {loading ? (
              <div className="outlet-deck-loading" role="status">
                {t('drawer.loading')}
              </div>
            ) : (
              // Not loading and nothing to show: the request failed.
              <DrillLoadError
                title={t('drillUi.outletLoadFailed')}
                onRetry={onRetry}
              />
            )}
          </div>
        )}
      </div>
    </>
  );
}

/**
 * The outlet drawer with its own metric: fetches the outlet-scoped drill
 * (compare settings included, so the trend keeps its compare line) and the
 * outlet's opportunity card for the active tab.
 */
export function OutletDeckDrawer({
  restaurantId,
  settings,
  regionFilter,
  source,
  status,
  shifts,
  outletId,
  outletName,
  outletMeta,
  initialMetricKey,
  periodLabel,
  connected,
  bucketLabels,
  aggregationLabel,
  comparable,
  onClose,
}: {
  restaurantId: string | undefined;
  settings: PerfSettings;
  regionFilter: string | null;
  source: SourceFilter | null;
  status: StatusFilter | null;
  shifts: ShiftFilter | null;
  outletId: string;
  outletName: string;
  outletMeta?: string;
  /** Metric to open on; falls back to covers. */
  initialMetricKey?: string | null;
  periodLabel: string;
  connected?: ConnectedSourceId[];
  bucketLabels?: string[];
  aggregationLabel?: string;
  comparable?: boolean;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<DrillTabDef>(() =>
    outletDeckStartTab(initialMetricKey),
  );
  const drill = useOpsPerformanceDrillDown(
    restaurantId,
    tab.key,
    settings,
    regionFilter,
    outletId,
    source,
    status,
    tab.secondaryKey ?? null,
    shifts,
  );
  const opportunity = useOpsOpportunityNarrative(
    restaurantId,
    tab.key,
    settings,
    regionFilter,
    true,
    outletId,
    source,
    status,
    shifts,
  );

  const pending = perfDrillIsPending(drill);
  const ready = perfDrillDataMatches(drill.data, tab.key) && !pending;
  // An aborted request is superseded by the next one: keep saying "loading".
  const loading = !ready && (pending || isMethodAbortError(drill.error));
  const opportunityForMetric = opportunity.data?.metricKey === tab.key;

  return (
    <OutletDeckDrawerView
      outletId={outletId}
      outletName={outletName}
      outletMeta={outletMeta}
      periodLabel={periodLabel}
      activeMetric={tab.key}
      onSwitchMetric={next => {
        if (next.key !== tab.key) setTab(next);
      }}
      connected={connected}
      data={ready ? drill.data : null}
      loading={loading}
      onRetry={() => {
        void drill.mutate();
      }}
      onClose={onClose}
      bucketLabels={bucketLabels}
      aggregationLabel={aggregationLabel}
      comparable={comparable}
      opportunityNarrative={
        opportunityForMetric ? (opportunity.data?.narrative ?? null) : null
      }
      opportunityNarrativeLoading={
        !opportunityForMetric &&
        (!!opportunity.isLoading || !!opportunity.isValidating)
      }
    />
  );
}
